<template>
    <div
        v-if="selectedJob"
        :key="selectedJob.uuid"
    >

        <div class="card shadow-sm d-none d-lg-block">

            <!-- ========================================= -->
            <!-- SCROLLABLE JOB DETAIL -->
            <!-- ========================================= -->
            <div class="job-detail-scroll fade-in">

                <!-- Header -->
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-start">
                        <div class="pr-3 flex-grow-1">
                            <h3 class="font-weight-bold mb-2">{{ selectedJob.name }}</h3>
                            <div
                                v-if="selectedJob.company"
                                class="mb-2"
                            >
                                <i class="mdi mdi-office-building mr-1 text-muted"></i>
                                <strong><CompanyLink :company="selectedJob.company" fallback="--" /></strong>
                            </div>
                            <div
                                v-if="selectedJob.location"
                                class="text-muted mb-2"
                            >
                                <i class="mdi mdi-map-marker-outline mr-1"></i>
                                {{ selectedJob.location || '--' }}
                            </div>
                            <div
                                v-if="selectedJob.department"
                                class="text-muted"
                            >
                                <i class="mdi mdi-account-group-outline mr-1"></i>
                                {{ selectedJob.department ? selectedJob.department.name : '--' }}
                            </div>
                        </div>
                        <div v-if="selectedJob.company?.logoUrl">
                            <img
                                :src="selectedJob.company?.logoUrl"
                                class="img-fluid"
                                style="width: 80px; height: 80px; object-fit: contain;"
                            >
                        </div>
                    </div>
                    <div class="mt-4">
                        <b-button
                            variant="info"
                            size="sm"
                            class="px-4"
                            v-on:click="formLink()"
                        >
                            <i class="mdi mdi-send mr-1"></i>
                            Apply Now
                        </b-button>
                    </div>
                </div>

                <!-- Job Details -->
                <div
                    v-if="selectedJob.minSalary || selectedJob.maxSalary || selectedJob.allowance || selectedJob.description || selectedJob.closingDate"
                    class="card-body border-top"
                >
                    <div class="row">

                        <!-- Salary -->
                        <div
                            v-if="selectedJob.minSalary && selectedJob.maxSalary"
                            class="col-md-6 mb-3"
                        >
                            <div class="d-flex align-items-start">
                                <div class="mr-3">
                                    <i class="mdi mdi-currency-usd h4 text-muted"></i>
                                </div>
                                <div>
                                    <small class="text-muted d-block">Salary Range</small>
                                    <div class="font-weight-bold">{{ salaryRange(selectedJob) }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Allowance -->
                        <div
                            v-if="selectedJob.allowance"
                            class="col-md-6 mb-3"
                        >
                            <div class="d-flex align-items-start">
                                <div class="mr-3">
                                    <i class="mdi mdi-cash-plus h4 text-muted"></i>
                                </div>
                                <div>
                                    <small class="text-muted d-block">Allowance</small>
                                    <div class="font-weight-bold">{{ selectedJob.allowance || '--' }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Fresh Graduate -->
                        <div
                            v-if="selectedJob.isFreshGraduate"
                            class="col-md-6 mb-3"
                        >
                            <div class="d-flex align-items-start">
                                <div class="mr-3">
                                    <i class="mdi mdi-school-outline h4 text-muted"></i>
                                </div>
                                <div>
                                    <small class="text-muted d-block">Fresh Graduate</small>
                                    <div>
                                        <i class="mdi mdi-check-circle-outline h5 text-success mb-0"></i>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Internship -->
                        <div
                            v-if="selectedJob.isIntern"
                            class="col-md-6 mb-3"
                        >
                            <div class="d-flex align-items-start">
                                <div class="mr-3">
                                    <i class="mdi mdi-briefcase-outline h4 text-muted"></i>
                                </div>
                                <div>
                                    <small class="text-muted d-block">Internship</small>
                                    <div>
                                        <i class="mdi mdi-check-circle-outline h5 text-success mb-0"></i>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Expiry -->
                        <div
                            v-if="selectedJob.closingDate"
                            class="col-md-6 mb-3"
                        >
                            <div class="d-flex align-items-start">
                                <div class="mr-3">
                                    <i class="mdi mdi-calendar h4 text-muted"></i>
                                </div>
                                <div>
                                    <small class="text-muted d-block">Expired Date</small>
                                    <div class="font-weight-bold">{{ selectedJob.closingDate || '--' }}</div>
                                </div>
                            </div>
                        </div>

                    </div>

                </div>

                <!-- Tags -->
                <div
                    v-if="selectedJob.tags.length > 0"
                    class="card-body border-top"
                >
                    <div class="d-flex flex-wrap">
                        <b-badge
                            v-for="(item, index) of selectedJob.tags"
                            :key="index"
                            variant="light"
                            class="border mr-2 mb-2 px-3 py-2"
                        >
                            <i class="mdi mdi-tag-outline mr-1 text-info"></i>
                            {{ item }}
                        </b-badge>
                    </div>
                </div>

                <!-- Description -->
                <div
                    v-if="selectedJob.description"
                    class="card-body border-top"
                >
                    <div v-html="selectedJob.description || '--'"></div>
                </div>

            </div>

        </div>

        <!-- ========================================================= -->
        <!-- MOBILE JOB DETAIL MODAL -->
        <!-- ========================================================= -->
        <b-modal
            id="job-detail-modal"
            v-if="selectedJob"
            size="lg"
            scrollable
            hide-footer
            title="Job Details"
            body-class="p-0"
            class="d-lg-none"
            centered
        >

            <!-- Header -->
            <div class="card-body">

                <div class="d-flex justify-content-between align-items-start">

                    <div class="pr-3 flex-grow-1">

                        <h4 class="font-weight-bold mb-2">
                            {{ selectedJob.name }}
                        </h4>


                        <div
                            v-if="selectedJob.company"
                            class="mb-2"
                        >
                            <i class="mdi mdi-office-building mr-1 text-muted"></i>

                            <strong>
                                <CompanyLink :company="selectedJob.company" fallback="--" />
                            </strong>
                        </div>


                        <div
                            v-if="selectedJob.location"
                            class="text-muted mb-2"
                        >
                            <i class="mdi mdi-map-marker-outline mr-1"></i>

                            {{ selectedJob.location || '--' }}
                        </div>


                        <div
                            v-if="selectedJob.department"
                            class="text-muted"
                        >
                            <i class="mdi mdi-account-group-outline mr-1"></i>

                            {{ selectedJob.department ? selectedJob.department.name : '--' }}
                        </div>

                    </div>


                    <!-- Company Logo -->
                    <div v-if="selectedJob.company?.logoUrl">

                        <img
                            :src="selectedJob.company?.logoUrl"
                            class="img-fluid"
                            style="
                                width: 70px;
                                height: 70px;
                                object-fit: contain;
                            "
                        >

                    </div>

                </div>


                <div class="mt-4">

                    <b-button
                        variant="info"
                        size="sm"
                        class="px-4"
                        v-on:click="formLink()"
                    >
                        <i class="mdi mdi-send mr-1"></i>
                        Apply Now
                    </b-button>

                </div>

            </div>


            <!-- ===================================================== -->
            <!-- JOB DETAILS -->
            <!-- ===================================================== -->
            <div
                v-if="
                    selectedJob.minSalary ||
                    selectedJob.maxSalary ||
                    selectedJob.allowance ||
                    selectedJob.closingDate ||
                    selectedJob.isFreshGraduate ||
                    selectedJob.isIntern
                "
                class="card-body border-top"
            >

                <div class="row">


                    <!-- Salary -->
                    <div
                        v-if="selectedJob.minSalary && selectedJob.maxSalary"
                        class="col-6 mb-3"
                    >

                        <div class="d-flex align-items-start">

                            <div class="mr-3">
                                <i class="mdi mdi-cash-multiple h4 text-muted"></i>
                            </div>

                            <div>
                                <small class="text-muted d-block">
                                    Salary Range
                                </small>

                                <div class="font-weight-bold">
                                    {{ salaryRange(selectedJob) }}
                                </div>
                            </div>

                        </div>

                    </div>


                    <!-- Allowance -->
                    <div
                        v-if="selectedJob.allowance"
                        class="col-6 mb-3"
                    >

                        <div class="d-flex align-items-start">

                            <div class="mr-3">
                                <i class="mdi mdi-cash-plus h4 text-muted"></i>
                            </div>

                            <div>
                                <small class="text-muted d-block">
                                    Allowance
                                </small>

                                <div class="font-weight-bold">
                                    {{ selectedJob.allowance || '--' }}
                                </div>
                            </div>

                        </div>

                    </div>


                    <!-- Location -->
                    <div
                        v-if="selectedJob.location"
                        class="col-12 mb-3"
                    >

                        <div class="d-flex align-items-start">

                            <div class="mr-3">
                                <i class="mdi mdi-map-marker-outline h4 text-muted"></i>
                            </div>

                            <div>
                                <small class="text-muted d-block">
                                    Location
                                </small>

                                <div class="font-weight-bold">
                                    {{ selectedJob.location || '--' }}
                                </div>
                            </div>

                        </div>

                    </div>


                    <!-- Fresh Graduate -->
                    <div
                        v-if="selectedJob.isFreshGraduate"
                        class="col-6 mb-3"
                    >

                        <div class="d-flex align-items-start">

                            <div class="mr-3">
                                <i class="mdi mdi-school-outline h4 text-muted"></i>
                            </div>

                            <div>
                                <small class="text-muted d-block">
                                    Fresh Graduate
                                </small>

                                <div>
                                    <i class="mdi mdi-check-circle-outline h5 text-success mb-0"></i>
                                </div>
                            </div>

                        </div>

                    </div>


                    <!-- Internship -->
                    <div
                        v-if="selectedJob.isIntern"
                        class="col-6 mb-3"
                    >

                        <div class="d-flex align-items-start">

                            <div class="mr-3">
                                <i class="mdi mdi-briefcase-outline h4 text-muted"></i>
                            </div>

                            <div>
                                <small class="text-muted d-block">
                                    Internship
                                </small>

                                <div>
                                    <i class="mdi mdi-check-circle-outline h5 text-success mb-0"></i>
                                </div>
                            </div>

                        </div>

                    </div>


                    <!-- Expiry -->
                    <div
                        v-if="selectedJob.closingDate"
                        class="col-6 mb-3"
                    >

                        <div class="d-flex align-items-start">

                            <div class="mr-3">
                                <i class="mdi mdi-calendar h4 text-muted"></i>
                            </div>

                            <div>
                                <small class="text-muted d-block">
                                    Expired Date
                                </small>

                                <div class="font-weight-bold">
                                    {{ selectedJob.closingDate || '--' }}
                                </div>
                            </div>

                        </div>

                    </div>

                </div>

            </div>


            <!-- ===================================================== -->
            <!-- TAGS -->
            <!-- ===================================================== -->
            <div
                v-if="selectedJob.tags && selectedJob.tags.length > 0"
                class="card-body border-top"
            >

                <div class="d-flex flex-wrap">

                    <b-badge
                        v-for="(item, index) of selectedJob.tags"
                        :key="index"
                        variant="light"
                        class="border mr-2 mb-2 px-3 py-2"
                    >
                        <i class="mdi mdi-tag-outline mr-1 text-info"></i>
                        {{ item }}
                    </b-badge>

                </div>

            </div>


            <!-- ===================================================== -->
            <!-- DESCRIPTION -->
            <!-- ===================================================== -->
            <div
                v-if="selectedJob.description"
                class="card-body border-top"
            >
                <div
                    v-html="selectedJob.description || '--'"
                ></div>

            </div>


            <!-- ===================================================== -->
            <!-- BOTTOM APPLY -->
            <!-- ===================================================== -->
            <!-- <div class="card-body border-top">

                <b-button
                    variant="info"
                    size="sm"
                    class="px-4"
                >
                    <i class="mdi mdi-send mr-1"></i>
                    Apply Now
                </b-button>

            </div> -->

        </b-modal>
    </div>
</template>
<script>
export default {
    props: {
        selectedJob: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {

        };
    },
    methods: {
        salaryRange(data) {
            if (data?.minSalary && data?.maxSalary) return `${data.minSalary} - ${data.maxSalary}`;
            if (data?.minSalary) return data.minSalary;
            if (data?.maxSalary) return data.maxSalary;
            return '--';
        },
        formLink() {
            const route = this.$router.resolve({ name: 'CandidateApplication' });
            const url = new URL(route.href, window.location.origin).href;
            // navigator.clipboard.writeText(url);
            // alertHelper.alertInfo({message: 'Copied!'});
            window.open(url, '_blank');
        },
    },
}
</script>
<style scoped>
    .job-detail-scroll {
        /* height: calc(100vh - 245px); */
        /* min-height: 600px; */
        height: calc(100vh - 230px);
        min-height: 500px;
        overflow-y: auto;
    }

    @media (max-width: 991.98px) {
        .job-detail-scroll {
            height: auto;
            min-height: 0;
            overflow-y: visible;
        }
    }
</style>
